import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Sparkles, Clock, Activity, PauseCircle } from "lucide-react";
import { api, formatDate, SUB_STATUS, PAYMENT_STATUS, PACKAGE_STYLE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { WelcomeHero } from "@/components/dashboard/WelcomeHero";
import { FinCards } from "@/components/fin/FinCards";

function HomeFinance() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/fin/summary").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return null;
  return (
    <section className="space-y-3" data-testid="home-finance">
      <div className="flex items-center justify-between"><h2 className="text-base md:text-lg font-semibold text-slate-900">Finansal özet</h2>
        <Link to="/admin/fin" className="text-sm text-emerald-700 font-semibold" data-testid="home-finance-link">Finans Merkezi →</Link></div>
      <FinCards s={s} only={["total_income", "total_expense", "net", "pending_collections", "subscription_income", "drone_income", "package_income", "refunds"]} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[["today", "Bugün"], ["week", "Bu hafta"], ["month", "Bu ay"], ["year", "Bu yıl"]].map(([k, l]) => (
        <Card key={k} className="p-3 text-sm" data-testid={`home-fin-${k}`}><span className="text-slate-500">{l} net:</span> <b>{new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" }).format(s.periods[k].net)}</b></Card>))}</div>
    </section>
  );
}

const Stat = ({ icon: Icon, label, value, tone, testId }) => (
  <Card data-testid={testId} className="p-5">
    <div className="flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{label}</div>
        <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</div>
      </div>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tone}`}><Icon className="w-5 h-5" /></div>
    </div>
  </Card>
);

const OfficeTable = ({ rows, empty, testId, extra }) => (
  <Card data-testid={testId} className="overflow-x-auto">
    {rows.length === 0 ? <div className="p-10 text-center text-slate-400 text-sm">{empty}</div> : (
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>{["Ofis", "Şehir", "Paket", "Durum", "Ödeme", "Kalan", extra?.label].filter(Boolean).map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.office_id} className="border-t hover:bg-slate-50">
              <td className="px-4 py-3"><div className="font-semibold">{r.office_name}</div><div className="font-mono text-xs text-slate-500">{r.office_code}</div></td>
              <td className="px-4 py-3 text-slate-600">{r.city}</td>
              <td className="px-4 py-3"><Badge variant="outline" className={PACKAGE_STYLE[r.package_code]}>{r.package_name}</Badge></td>
              <td className="px-4 py-3">{r.status && <Badge variant="outline" className={SUB_STATUS[r.status].cls}>{SUB_STATUS[r.status].label}</Badge>}</td>
              <td className="px-4 py-3">{PAYMENT_STATUS[r.payment_status]}</td>
              <td className="px-4 py-3 font-semibold">{r.status === "FROZEN" ? "-" : `${r.days_left} gün`}</td>
              {extra && <td className="px-4 py-3 text-xs text-slate-600">{extra.render(r)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </Card>
);

export default function AdminDashboard() {
  const { user, hasPerm } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (hasPerm("OFFICE_VIEW")) api.get("/admin/overview").then(({ data }) => setData(data));
  }, []);

  const lastActivity = { label: "Son İşlem", render: (r) => (r.last_activity ? new Date(r.last_activity).toLocaleString("tr-TR") : "Henüz yok") };

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <WelcomeHero eyebrow="Platform Paneli" name={user?.full_name}
        subtitle="Ofislerin abonelik ve kullanım durumu. Ofislerin gelir/gider verileri bu panelde gösterilmez."
        actions={[
          hasPerm("OFFICE_VIEW") && { to: "/offices", label: "Emlak Ofisleri", icon: Building2, testId: "hero-offices" },
          hasPerm("SUBSCRIPTION_VIEW") && { to: "/admin/subscriptions", label: "Abonelik Takibi", icon: Clock, testId: "hero-subscriptions" },
        ].filter(Boolean)} />

      {(hasPerm("FIN_VIEW") || user?.role === "SUPER_ADMIN") && <HomeFinance />}

      {!data ? (
        <Card className="p-10 text-center text-slate-500">
          {hasPerm("OFFICE_VIEW") ? "Yükleniyor..." : <>Bu hesapta ofis görüntüleme izni yok. {hasPerm("FINANCE_REPORTS") && <Link className="text-emerald-700 underline" to="/admin/finance">Platform Finans</Link>}</>}
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Stat testId="overview-total" icon={Building2} label="Toplam Ofis" value={data.counts.total} tone="bg-slate-100 text-slate-700" />
            <Stat testId="overview-new" icon={Sparkles} label="Bu Hafta Eklenen" value={data.counts.new_this_week} tone="bg-blue-100 text-blue-700" />
            <Stat testId="overview-expiring" icon={Clock} label="Süresi Doluyor" value={data.counts.expiring} tone="bg-amber-100 text-amber-700" />
            <Stat testId="overview-active" icon={Activity} label="Aktif" value={data.counts.active} tone="bg-emerald-100 text-emerald-700" />
            <Stat testId="overview-passive" icon={PauseCircle} label="Pasif" value={data.counts.passive} tone="bg-red-100 text-red-700" />
          </div>

          <Tabs defaultValue="new">
            <TabsList>
              <TabsTrigger data-testid="tab-new" value="new">Bu Hafta Eklenenler</TabsTrigger>
              <TabsTrigger data-testid="tab-expiring" value="expiring">Süresi Dolmaya Yaklaşanlar</TabsTrigger>
              <TabsTrigger data-testid="tab-active" value="active">Aktif Ofisler</TabsTrigger>
              <TabsTrigger data-testid="tab-passive" value="passive">Pasif Ofisler</TabsTrigger>
              <TabsTrigger data-testid="tab-all" value="all">Tüm Paket & Ödemeler</TabsTrigger>
            </TabsList>
            <TabsContent value="new" className="mt-4">
              <OfficeTable testId="table-new" rows={data.new_this_week} empty="Bu hafta yeni ofis eklenmedi." extra={{ label: "Eklenme", render: (r) => formatDate(r.created_at) }} />
            </TabsContent>
            <TabsContent value="expiring" className="mt-4">
              <OfficeTable testId="table-expiring" rows={data.expiring} empty="Süresi 7 gün içinde dolacak ofis yok." extra={{ label: "Bitiş", render: (r) => formatDate(r.period_end) }} />
            </TabsContent>
            <TabsContent value="active" className="mt-4">
              <OfficeTable testId="table-active" rows={data.active} empty="Aktif ofis yok." extra={lastActivity} />
            </TabsContent>
            <TabsContent value="passive" className="mt-4">
              <OfficeTable testId="table-passive" rows={data.passive} empty="Pasif (dondurulmuş) ofis yok." extra={{ label: "Bitiş", render: (r) => formatDate(r.period_end) }} />
            </TabsContent>
            <TabsContent value="all" className="mt-4">
              <OfficeTable testId="table-all" rows={data.all} empty="Ofis yok." extra={lastActivity} />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
